/* @jsx h */
import { defineComponent, h } from "vue";
import { Loading } from "@app/vue/components/index";
import {
  PaymentsSummaryReport,
  OrdersHourlyReportDatum,
  OrdersBreakdown,
} from "@lemmonhq/types";
import { SummaryStats } from "./components/SummaryStats";
import { PaymentMethods } from "./components/PaymentMethods";
import { TopProducts } from "./components/TopProducts";
import { TopCategories } from "./components/TopCategories";
import type { TopProductsDisplayDatum } from "./components/TopProducts";
import type { TopCategoriesDisplayDatum } from "./components/TopCategories";
import { HourlySales } from "./components/HourlySales";
import { OrderTypes } from "./components/OrderTypes";
import { DateRange } from "@lemmonhq/uikit";

export type OverviewChartProps = {
  data: PaymentsSummaryReport;
  hourlyData: OrdersHourlyReportDatum[];
  ordersBreakdown: OrdersBreakdown;
  topProducts: TopProductsDisplayDatum[];
  topCategories: TopCategoriesDisplayDatum[];
  loading: boolean;
  dateRange: DateRange;
  currency: string;
  showDetailLinks?: boolean;
};

export const OverviewChart = defineComponent<OverviewChartProps>({
  name: "OverviewChart",
  props: [
    "data",
    "hourlyData",
    "ordersBreakdown",
    "topProducts",
    "topCategories",
    "loading",
    "dateRange",
    "currency",
    "showDetailLinks",
  ],
  setup(props) {
    return () => {
      if (props.loading) {
        return (
          <div class="w-full h-[200px] flex justify-center items-center">
            <Loading />
          </div>
        );
      }
      return (
        <div class="space-y-4 md:space-y-6">
          <SummaryStats
            data={props.data}
            ordersBreakdown={props.ordersBreakdown}
            currency={props.currency}
            showExportLink={props.showDetailLinks}
          />

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <PaymentMethods data={props.data} currency={props.currency} />
            <OrderTypes
              data={props.ordersBreakdown}
              totalAmount={props.data.total_amount}
              currency={props.currency}
            />
            <TopProducts
              data={props.topProducts}
              totalAmount={props.data.total_amount}
              dateRange={props.dateRange}
              currency={props.currency}
              showDetailLink={props.showDetailLinks}
            />
            <TopCategories
              data={props.topCategories}
              totalAmount={props.data.total_amount}
              dateRange={props.dateRange}
              currency={props.currency}
              showDetailLink={props.showDetailLinks}
            />
            <HourlySales data={props.hourlyData} currency={props.currency} />
          </div>
        </div>
      );
    };
  },
});
